import { useState, useEffect } from 'react';
import { supabase } from '../auth/secureSupabase';
import { isSandboxMode, sandboxAuthBypass, getSandboxSession } from '../auth/sandboxAuth';

export default function AuthWrapper({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // SANDBOX MODE: Bypass authentication
    if (isSandboxMode()) {
      console.log('🏖️ AuthWrapper: SANDBOX MODE - Using auth bypass');

      const initSandbox = async () => {
        try {
          let sandboxData = getSandboxSession();

          if (!sandboxData) {
            console.log('🔓 Creating sandbox session...');
            sandboxData = await sandboxAuthBypass();
          }

          if (sandboxData?.user) {
            console.log('✅ Sandbox auth successful:', sandboxData.user.email);
            setUser(sandboxData.user);
          }

          setLoading(false);
        } catch (error) {
          console.error('❌ Sandbox auth failed:', error);
          setLoading(false);
        }
      };

      initSandbox();
      return; // Skip normal auth flow
    }

    // PRODUCTION MODE: Normal auth flow
    // Listen for session from parent window (PostMessage)
    const handleMessage = async (event) => {
      if (event.origin !== 'https://atsinsiderai.com' && event.origin !== 'https://www.atsinsiderai.com') return;

      // Handle handshake - app is ready to receive session
      if (event.data.type === 'APP_READY_CHECK') {
        console.log('🤝 Received readiness check, responding with APP_READY');
        event.source.postMessage({ type: 'APP_READY' }, event.origin);
        return;
      }

      // Handle session data
      if (event.data.type === 'SUPABASE_SESSION' && event.data.session) {
        console.log('📨 Received session via PostMessage');

        try {
          // Set session in Supabase
          await supabase.auth.setSession(event.data.session);
          setUser(event.data.session.user);
          setLoading(false);

          // Send acknowledgment back to sender (if there is one)
          if (event.source) {
            console.log('✅ Session set successfully, sending ACK');
            event.source.postMessage({
              type: 'SESSION_ACK',
              success: true
            }, event.origin);
          } else {
            console.log('✅ Session set successfully (no source to ACK)');
          }
        } catch (error) {
          console.error('❌ Failed to set session:', error);
          // Send error acknowledgment (if there is a source)
          if (event.source) {
            event.source.postMessage({
              type: 'SESSION_ACK',
              success: false,
              error: error.message
            }, event.origin);
          }
        }
      }
    };

    window.addEventListener('message', handleMessage);

    // Get initial session
    const getInitialSession = async () => {
      console.log('🔍 AuthWrapper: Checking for session...');

      const { data: { session }, error } = await supabase.auth.getSession();

      console.log('📊 AuthWrapper session data: user =', session?.user?.email);
      if (error) {
        console.log('❌ AuthWrapper session error:', error);
      }

      if (error) {
        console.error('Error getting session:', error);
      }

      if (session?.user) {
        console.log('✅ AuthWrapper: User found -', session.user.email);
        setUser(session.user);
        setLoading(false);
      } else {
        console.log('❌ AuthWrapper: No user session found, waiting for PostMessage...');
        // Don't set loading to false yet, wait for potential PostMessage
        // Extended timeout to allow for retry mechanism
        setTimeout(() => {
          if (!user) {
            console.log('⏰ Timeout reached, no session received via PostMessage');
            setLoading(false);
          }
        }, 6000);
      }
    };

    getInitialSession();

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth state changed:', event, session?.user?.email);
        setUser(session?.user || null);
        setLoading(false);
      }
    );

    return () => {
      subscription?.unsubscribe();
      window.removeEventListener('message', handleMessage);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-white mb-2">Loading...</h2>
          <p className="text-gray-400">Checking authentication status</p>
        </div>
      </div>
    );
  }

  if (!user) {
    // No authentication UI here - redirect to main website
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-8">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-white mb-4">Session Expired</h2>
          <p className="text-gray-400 mb-6">
            Your session has expired. Please sign in again to continue using ATS Insider.
          </p>
          <div className="space-y-3">
            <button
              onClick={() => {
                // Clear any stale tokens and redirect to sign in
                localStorage.removeItem('supabase.auth.token');
                sessionStorage.clear();
                window.location.href = 'https://atsinsiderai.com/login';
              }}
              className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
            >
              Sign In Again →
            </button>
            <button
              onClick={() => {
                // Clear tokens and try to retry
                localStorage.removeItem('supabase.auth.token');
                sessionStorage.clear();
                window.location.reload();
              }}
              className="w-full bg-gray-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-gray-700 transition-colors"
            >
              Clear Session & Retry
            </button>
          </div>
          <p className="text-gray-500 text-sm mt-4">
            This happens when your login session expires for security. Your data is safe.
          </p>
        </div>
      </div>
    );
  }

  // User is authenticated, show the app
  return children;
}